import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { API_BASE_URL } from '@synergy/config';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { BonusSettlementDetailFormValues } from '../../models/bonus-settlement-detail-form-values';
import { BonusSettlementsApi } from '../../api/bonus-settlements-api';

const api = new BonusSettlementsApi(API_BASE_URL);

interface SaveBonusSettlementDetailParams {
    bonusSettlementId: number;
    values: BonusSettlementDetailFormValues;
    shouldSubmit?: boolean;
}

interface SubmitBonusSettlementDetailParams {
    bonusSettlementId: number;
    values: BonusSettlementDetailFormValues;
}

interface UpdateNoteFromSupplierParams {
    bonusSettlementId: number;
    note: string;
}

interface UpdateBonusSettlementSupplierFilesParams {
    bonusSettlementId: number;
    files: File[];
    deletedFileIds: number[];
}

interface UpdateCustomerParams {
    supplierBonusSettlementId: number;
    customerId: number;
}

export const getBonusSettlementDetail = createAsyncThunk(
    'bonus-settlements/getBonusSettlementDetail',
    async (bonusSettlementId: number, thunkAPI) => {
        const response = await api.BonusSettlementsDetail.getBonusSettlementDetail(bonusSettlementId);
        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return null;
        }
        return response.result;
    }
);

export const saveBonusSettlementDetail = createAsyncThunk(
    'bonus-settlements/saveBonusSettlementDetail',
    async ({ bonusSettlementId, values, shouldSubmit }: SaveBonusSettlementDetailParams, thunkAPI) => {
        const response = await api.BonusSettlementsDetail.saveBonusSettlementDetail(bonusSettlementId, values);
        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return null;
        }

        if (!shouldSubmit) {
            thunkAPI.dispatch(
                Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.bonusSettlements.saveSuccess'))
            );
        }
        return null;
    }
);

export const submitBonusSettlementDetail = createAsyncThunk(
    'bonus-settlements/submitBonusSettlementDetail',
    async ({ bonusSettlementId, values }: SubmitBonusSettlementDetailParams, thunkAPI) => {
        let response;

        response = await api.BonusSettlementsDetail.saveBonusSettlementDetail(bonusSettlementId, values);
        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        response = await api.BonusSettlementsDetail.submitBonusSettlementDetail(bonusSettlementId);
        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.bonusSettlements.submitSuccess'))
        );
        return null;
    }
);

export const updateNoteFromSupplier = createAsyncThunk(
    'bonus-settlements/updateNoteFromSupplier',
    async ({ bonusSettlementId, note }: UpdateNoteFromSupplierParams, thunkAPI) => {
        const response = await api.BonusSettlementsDetail.updateNoteFromSupplier(bonusSettlementId, note);
        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.bonusSettlements.updateNoteSuccess'))
        );
        return note;
    }
);

export const updateSupplierBonusSettlementFiles = createAsyncThunk(
    'bonus-settlements/uploadFiles',
    async (params: UpdateBonusSettlementSupplierFilesParams, thunkAPI) => {
        const responses = await Promise.all(
            params.files.map(file => api.BonusSettlementsDetail.uploadFile(params.bonusSettlementId, file))
        );
        let deleteResponse;

        if (params.deletedFileIds.length > 0) {
            deleteResponse = await api.BonusSettlementsDetail.deleteFiles(
                params.bonusSettlementId,
                params.deletedFileIds
            );
        }

        const allResponsesSuccessful = responses.every(response => response.success);
        const isDeleteResponseSuccessful = deleteResponse ? deleteResponse.success : true;

        if (!allResponsesSuccessful || !isDeleteResponseSuccessful) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));

            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.filesUpdated')));

        return responses;
    }
);

export const resetSubmittedSuccessModal = createAction('bonus-settlements/resetSubmittedSuccessModal');

export const resetBonusSettlementDetail = createAction('bonus-settlements/resetBonusSettlementDetail');

export const addCustomer = createAsyncThunk(
    'bonus-settlements/addCustomer',
    async (params: UpdateCustomerParams, thunkAPI) => {
        const response = await api.BonusSettlementsDetail.addCustomer(
            params.supplierBonusSettlementId,
            params.customerId
        );

        if (!response.success) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                Snackbar.PoolNotification.success(
                    'app.snackbar.updateBonusSettlementSupplierDetailSupplierAddCustomerSuccess'
                )
            )
        );

        return response.result;
    }
);

export const removeCustomer = createAsyncThunk(
    'bonus-settlements/removeCustomer',
    async (params: UpdateCustomerParams, thunkAPI) => {
        const response = await api.BonusSettlementsDetail.deleteCustomer(
            params.supplierBonusSettlementId,
            params.customerId
        );

        if (!response.success) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.customerDeleted')));

        return params.customerId;
    }
);
